import React, { useEffect, useState } from 'react';
import { Database, Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Admin "Original Files" config — lets the operator tweak intro/attention
 * copy, the eight column labels of the search-results table, the buy-
 * confirmation modal text (3 checkboxes + buttons) and the global default
 * file price. Settings are stored as a single doc keyed
 * "original_files_settings" inside `app_config`.
 */
export default function OriginalFilesAdmin({ token }) {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/original-files/admin/settings`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (r.ok) setS(await r.json());
      } finally { setLoading(false); }
    })();
  }, [token]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/original-files/admin/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          enabled: s.enabled,
          default_price: Number(s.default_price) || 0,
          currency: s.currency,
          intro_text: s.intro_text,
          attention_text: s.attention_text,
          column_labels: s.column_labels,
          buy_modal: s.buy_modal,
        }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setS(await r.json());
      toast.success('Settings saved');
    } catch (e) {
      toast.error('Save failed', { description: e.message });
    } finally { setSaving(false); }
  };

  if (loading) return <div className="p-8 text-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Loading…</div>;
  if (!s) return <div className="p-8 text-center text-rose-600">Failed to load settings.</div>;

  const upd = (path, value) => {
    setS((prev) => {
      const next = { ...prev };
      const segs = path.split('.');
      let cur = next;
      for (let i = 0; i < segs.length - 1; i++) {
        cur[segs[i]] = { ...(cur[segs[i]] || {}) };
        cur = cur[segs[i]];
      }
      cur[segs.at(-1)] = value;
      return next;
    });
  };

  return (
    <div className="space-y-5" data-testid="orig-files-admin">
      <header className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2"><Database className="w-5 h-5 text-cyan-600" /> Original Files</h2>
        <button
          onClick={save} disabled={saving}
          className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg font-bold text-sm flex items-center gap-2 disabled:opacity-50"
          data-testid="orig-admin-save"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save
        </button>
      </header>

      {/* Toggle + price */}
      <Card title="General">
        <div className="grid sm:grid-cols-3 gap-3">
          <label className="flex items-center gap-2 text-sm font-medium">
            <input type="checkbox" checked={!!s.enabled} onChange={(e) => upd('enabled', e.target.checked)} data-testid="orig-admin-enabled" />
            Store enabled
          </label>
          <label className="block">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Default price (€)</span>
            <input
              type="number" step="0.01" value={s.default_price ?? 20}
              onChange={(e) => upd('default_price', parseFloat(e.target.value) || 0)}
              className="mt-1 w-full border border-slate-300 rounded-lg px-2.5 py-1.5 text-sm font-mono"
              data-testid="orig-admin-price"
            />
          </label>
          <label className="block">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Currency</span>
            <input
              type="text" value={s.currency || 'EUR'} onChange={(e) => upd('currency', e.target.value)}
              className="mt-1 w-full border border-slate-300 rounded-lg px-2.5 py-1.5 text-sm font-mono"
              data-testid="orig-admin-currency"
            />
          </label>
        </div>
      </Card>

      {/* Intro / attention */}
      <Card title="Customer-facing copy" subtitle="HTML allowed (b, i, a, p, ul, li…)">
        <Field label="Intro text">
          <textarea rows={4} value={s.intro_text || ''} onChange={(e) => upd('intro_text', e.target.value)} data-testid="orig-admin-intro" />
        </Field>
        <Field label="Attention / disclaimer text (red banner)">
          <textarea rows={3} value={s.attention_text || ''} onChange={(e) => upd('attention_text', e.target.value)} data-testid="orig-admin-attention" />
        </Field>
      </Card>

      {/* Column labels */}
      <Card title="Search results — column labels">
        <div className="grid sm:grid-cols-2 gap-2.5">
          {Object.entries(s.column_labels || {}).map(([k, v]) => (
            <label key={k} className="block">
              <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{k.replace(/_/g, ' ')}</span>
              <input
                type="text" value={v || ''}
                onChange={(e) => upd(`column_labels.${k}`, e.target.value)}
                className="mt-1 w-full border border-slate-300 rounded-lg px-2.5 py-1.5 text-sm"
                data-testid={`orig-admin-col-${k}`}
              />
            </label>
          ))}
        </div>
      </Card>

      {/* Buy modal */}
      <Card title="Purchase confirmation modal">
        <Field label="Modal title">
          <input type="text" value={s.buy_modal?.title || ''} onChange={(e) => upd('buy_modal.title', e.target.value)} data-testid="orig-admin-modal-title" />
        </Field>
        <Field label="Intro (HTML)">
          <textarea rows={2} value={s.buy_modal?.intro || ''} onChange={(e) => upd('buy_modal.intro', e.target.value)} data-testid="orig-admin-modal-intro" />
        </Field>
        <Field label="Checkbox 1 (HTML)">
          <textarea rows={2} value={s.buy_modal?.checkbox_1 || ''} onChange={(e) => upd('buy_modal.checkbox_1', e.target.value)} data-testid="orig-admin-modal-cb1" />
        </Field>
        <Field label="Checkbox 2 (HTML)">
          <textarea rows={2} value={s.buy_modal?.checkbox_2 || ''} onChange={(e) => upd('buy_modal.checkbox_2', e.target.value)} data-testid="orig-admin-modal-cb2" />
        </Field>
        <Field label="Checkbox 3 (HTML)">
          <textarea rows={2} value={s.buy_modal?.checkbox_3 || ''} onChange={(e) => upd('buy_modal.checkbox_3', e.target.value)} data-testid="orig-admin-modal-cb3" />
        </Field>
        <Field label="Checkbox 4 (HTML) — optional; leave blank to hide">
          <textarea rows={3} value={s.buy_modal?.checkbox_4 || ''} onChange={(e) => upd('buy_modal.checkbox_4', e.target.value)} data-testid="orig-admin-modal-cb4" />
        </Field>
        <div className="grid sm:grid-cols-2 gap-2.5">
          <Field label="'Buy' button label">
            <input type="text" value={s.buy_modal?.buy_label || ''} onChange={(e) => upd('buy_modal.buy_label', e.target.value)} data-testid="orig-admin-modal-buy-label" />
          </Field>
          <Field label="'Cancel' button label">
            <input type="text" value={s.buy_modal?.cancel_label || ''} onChange={(e) => upd('buy_modal.cancel_label', e.target.value)} data-testid="orig-admin-modal-cancel-label" />
          </Field>
        </div>
        <Field label="Success toast text (after purchase)">
          <textarea rows={2} value={s.buy_modal?.success_text || ''} onChange={(e) => upd('buy_modal.success_text', e.target.value)} data-testid="orig-admin-modal-success" />
        </Field>
      </Card>
    </div>
  );
}

function Card({ title, subtitle, children }) {
  return (
    <section className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 sm:p-5">
      <h3 className="font-semibold text-slate-800 mb-3">
        {title}
        {subtitle && <span className="ml-2 text-[11px] font-normal text-slate-500">{subtitle}</span>}
      </h3>
      <div className="space-y-3">{children}</div>
    </section>
  );
}
function Field({ label, children }) {
  // Apply consistent styling to whatever input/textarea sits inside.
  const child = React.Children.only(children);
  const cls = 'mt-1 w-full border border-slate-300 rounded-lg px-2.5 py-1.5 text-sm font-mono';
  const cloned = React.cloneElement(child, { className: cls });
  return (
    <label className="block">
      <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{label}</span>
      {cloned}
    </label>
  );
}

